// Thin wrapper around Amplify v6 Cognito auth. Amplify owns token storage,
// silent refresh, and the `?code=` exchange. We only expose the small surface
// the rest of the app needs: read current id/access token, force-refresh, and
// sign out. All getters are async because Amplify's session APIs are Promise-
// based; callers (axios interceptor, TopNavbar logout) already await.

import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { environment } from "src/config/environment";

const safeFetchSession = async (forceRefresh = false) => {
	try {
		return await fetchAuthSession(forceRefresh ? { forceRefresh: true } : undefined);
	} catch (error) {
		console.error("fetchAuthSession failed:", error);
		return null;
	}
};

/** Returns the current id_token JWT, or null if there's no Cognito session. */
export const getCognitoToken = async (): Promise<string | null> => {
	const session = await safeFetchSession();
	return session?.tokens?.idToken?.toString() ?? null;
};

/** Returns the current access_token JWT, or null if there's no Cognito session. */
export const getCognitoAccessToken = async (): Promise<string | null> => {
	const session = await safeFetchSession();
	return session?.tokens?.accessToken?.toString() ?? null;
};

/**
 * Returns a valid access token, refreshing internally if Amplify's own buffer
 * has expired. Used by the axios request interceptor to attach the Bearer token.
 */
export const ensureValidCognitoToken = async (): Promise<string | null> => {
	const session = await safeFetchSession();
	return session?.tokens?.accessToken?.toString() ?? null;
};

/**
 * Force a refresh through Amplify, bypassing its in-memory cache. Used by the
 * 401 response interceptor as a one-time retry before giving up and redirecting
 * to /login.
 */
export const forceRefreshCognitoToken = async (): Promise<string | null> => {
	const session = await safeFetchSession(true);
	return session?.tokens?.accessToken?.toString() ?? null;
};

/**
 * Read the current Cognito refresh token from Amplify's default (localStorage)
 * token store. Amplify v6 deliberately does NOT surface the refresh token via
 * `fetchAuthSession` (only id/access tokens), so we read the documented storage
 * keys it writes:
 *   CognitoIdentityServiceProvider.<clientId>.LastAuthUser           -> username
 *   CognitoIdentityServiceProvider.<clientId>.<username>.refreshToken -> token
 * Returns null when there's no stored session or storage is unavailable.
 */
const getCognitoRefreshToken = (): string | null => {
	const clientId = environment.REACT_APP_COGNITO_CLIENT_ID;
	if (!clientId) return null;
	try {
		const prefix = `CognitoIdentityServiceProvider.${clientId}`;
		const lastUser = localStorage.getItem(`${prefix}.LastAuthUser`);
		if (!lastUser) return null;
		return localStorage.getItem(`${prefix}.${lastUser}.refreshToken`);
	} catch {
		// localStorage unavailable (private mode / SSR) — nothing to revoke.
		return null;
	}
};

/**
 * Explicitly revoke the refresh token (and every access/id token derived from
 * it) at Cognito's `/oauth2/revoke` endpoint.
 *
 * Why this is done manually instead of via `signOut({ global: true })`:
 * `global: true` routes through Cognito's GlobalSignOut API, which silently
 * no-ops for our federated Hosted-UI / external-IdP sessions (Amplify swallows
 * the failure — see its `globalSignOut` "it should not throw" path). The result
 * was that tokens were never actually revoked server-side. Hitting the revoke
 * endpoint directly guarantees revocation regardless of how the user signed in.
 *
 * Best-effort: a failed revoke must not block the subsequent cookie teardown.
 */
const revokeCognitoRefreshToken = async (): Promise<void> => {
	const domain = environment.REACT_APP_COGNITO_DOMAIN;
	const clientId = environment.REACT_APP_COGNITO_CLIENT_ID;
	const refreshToken = getCognitoRefreshToken();
	if (!domain || !clientId || !refreshToken) return;

	try {
		await fetch(`https://${domain}/oauth2/revoke`, {
			method: "POST",
			headers: { "Content-Type": "application/x-www-form-urlencoded" },
			body: new URLSearchParams({ token: refreshToken, client_id: clientId }).toString(),
		});
	} catch (error) {
		console.error("Cognito token revoke failed:", error);
	}
};

/**
 * Full sign-out for an explicit user "Log out". Two steps:
 *
 * 1. Revoke the refresh token server-side via Cognito's `/oauth2/revoke`
 *    endpoint (see revokeCognitoRefreshToken). This invalidates the refresh
 *    token and all tokens minted from it.
 * 2. `signOut()` clears Amplify's local tokens and — because OAuth is configured
 *    (see amplifyConfig.ts) — redirects the browser through Cognito's Hosted UI
 *    `/logout` endpoint back to the configured `redirectSignOut` (/login). That
 *    tears down the Cognito Hosted-UI session cookie so a surviving session
 *    can't silently re-authenticate the user on the next entry (critical on
 *    shared machines).
 *
 * Revocation is handled in step 1, so we use the non-global `signOut()` here:
 * `global: true` only adds the GlobalSignOut API call that no-ops for federated
 * sessions anyway.
 *
 * NOTE: callers must NOT issue their own `window.location` redirect after
 * awaiting this — doing so clobbers Amplify's in-flight logout navigation and
 * leaves the Hosted-UI session intact (the original logout bug).
 */
/**
 * Revoke tokens and clear Amplify's local storage WITHOUT triggering the
 * Hosted-UI /logout redirect. Use when the page must stay put (e.g. the
 * account-mismatch screen) but the session must be invalidated so duplicate
 * tabs can't silently authenticate.
 */
export const revokeCognitoSession = async (): Promise<void> => {
	await revokeCognitoRefreshToken();
	const clientId = environment.REACT_APP_COGNITO_CLIENT_ID;
	if (!clientId) return;
	try {
		const prefix = `CognitoIdentityServiceProvider.${clientId}`;
		const keys = Object.keys(localStorage).filter((k) => k.startsWith(prefix));
		keys.forEach((k) => localStorage.removeItem(k));
	} catch {
		// localStorage unavailable — non-fatal.
	}
};

export const clearCognitoToken = async (): Promise<void> => {
	await revokeCognitoRefreshToken();
	try {
		await signOut();
	} catch (error) {
		console.error("Amplify signOut failed:", error);
	}
};
